/**
 * @file        Labels.jsx
 * @module      modules/kyb/manager/components
 * @description Small shared pieces of the KYB Manager: status tags, the four
 *              checks, date formatting and a read-only JSON block.
 */

import React from "react";
import { Space, Tag, Tooltip, Typography } from "antd";
import { CHECK_KEYS, STATUS_LABEL } from "../../utils/kybConstants";

export const CHECK_TITLE = { business: "Business", individual: "Identity", association: "Link", authority: "Authority" };

const CHECK_COLOR = {
  not_started: "default",
  in_progress: "processing",
  awaiting_approval: "gold",
  verified: "success",
  review_required: "warning",
  not_matched: "orange",
  declined: "error",
};

export const OUTCOME = {
  open: { label: "In progress", color: "processing" },
  approved: { label: "Approved", color: "success" },
  in_review: { label: "In review", color: "warning" },
  declined: { label: "Declined", color: "error" },
  abandoned: { label: "Abandoned", color: "default" },
};

export const REVIEW_STATUS = {
  open: { label: "Open", color: "warning" },
  approved: { label: "Approved", color: "success" },
  declined: { label: "Declined", color: "error" },
  superseded: { label: "Superseded", color: "default" },
};

export const JOIN_STATUS = {
  awaiting: { label: "Awaiting business admin", color: "warning" },
  approved: { label: "Approved", color: "success" },
  declined: { label: "Declined", color: "error" },
  cancelled: { label: "Cancelled", color: "default" },
};

export const BUSINESS_STATUS = {
  verified: { label: "Verified", color: "success" },
  unverified: { label: "Not verified yet", color: "default" },
  review_required: { label: "Review required", color: "warning" },
};

export function StatusTag({ map, value }) {
  const s = map[value || "open"] || { label: value || "—", color: "default" };
  return <Tag color={s.color}>{s.label}</Tag>;
}

export function CheckTag({ check, value }) {
  return (
    <Tooltip title={`${CHECK_TITLE[check]}: ${STATUS_LABEL[value] || value}`}>
      <Tag color={CHECK_COLOR[value] || "default"}>{CHECK_TITLE[check]}</Tag>
    </Tooltip>
  );
}

export function Checks({ checks = {}, showStatus = false }) {
  return (
    <Space size={[4, 4]} wrap>
      {CHECK_KEYS.map((k) =>
        showStatus ? (
          <Tag key={k} color={CHECK_COLOR[checks[k]] || "default"}>
            {CHECK_TITLE[k]}: {STATUS_LABEL[checks[k]] || checks[k] || "—"}
          </Tag>
        ) : (
          <CheckTag key={k} check={k} value={checks[k]} />
        ),
      )}
    </Space>
  );
}

const pad = (n) => String(n).padStart(2, "0");
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "27 Oct 2026, 14:05" in the viewer's time zone. */
export function fmtDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fmtSize(bytes) {
  if (!bytes) return "";
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;
}

export const dash = (v) => (v === null || v === undefined || v === "" ? "—" : v);

export function Person({ user }) {
  if (!user) return "—";
  return (
    <div>
      <div>{user.name || <Typography.Text type="secondary">Unknown user</Typography.Text>}</div>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {[user.email, user.mobile].filter(Boolean).join(" · ") || user.id}
      </Typography.Text>
    </div>
  );
}

export function BusinessCell({ business }) {
  if (!business) return <Typography.Text type="secondary">Not entered yet</Typography.Text>;
  return (
    <div>
      <div>{business.name || "—"}</div>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {[business.pan, business.entityType].filter(Boolean).join(" · ")}
      </Typography.Text>
    </div>
  );
}

export function RawJson({ value }) {
  return <pre className="kybm-json">{JSON.stringify(value ?? null, null, 2)}</pre>;
}
